import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import ProductVariant from '@/modules/product/components/ProductVariant.vue';
import AddToCartToast from '@/modules/cart/AddToCartToast.vue';
import AuthModal from '@/modules/auth/containers/AuthModal.vue';
import ProductUnit from '@/modules/product/components/ProductUnit.vue';
import PriceDatePicker from '@/modules/product/components/PriceDatePicker.vue';
function fromISO(iso) {
    if (!iso)
        return null;
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
}
function toISO(date) {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
export default Vue.extend({
    components: { ProductVariant, AddToCartToast, AuthModal, ProductUnit, PriceDatePicker },
    computed: {
        ...mapState('productModule', ['currentProduct', 'currentVariant']),
        ...mapState('authModule', ['isLoggedIn']),
        //...mapState('wishlistModule', ['allItems', 'itemsWishlist']),
        ...mapState('cartModule', ['items', 'cartMessages']),
        // ...mapGetters('wishlistModule', ['isOnWishlist']),
        ...mapGetters('settingsModule', ['getSettingByKey']),
        get_it_preorder_item_notice() {
            return this.getSettingByKey('get_it_preorder_item_notice');
        },
        discountPercent() {
            const original = Number(this.product?.old_price);
            const discounted = Number(this.product?.price);
            if (!original || !discounted || discounted >= original)
                return 0;
            return Math.round(((original - discounted) / original) * 100);
        },
        cartItems() {
            return this.items;
        },
        product() {
            return this.currentProduct;
        },
        selectedVariant() {
            return this.currentVariant;
        },
        // true if all units are out of stock (considering what's already in the cart)
        isAllSelectedOutOfStock() {
            if (!this.selectedVariant?.units?.length)
                return true;
            return !this.selectedVariant.units.some(u => this.getUnitRemainingStock(u) > 0);
        },
        // ✅ CHANGED: require date when dynamic pricing is used
        isAddToCartDisabled() {
            if (this.disableAddToCart)
                return true;
            if (!this.selectedVariant)
                return true;
            if (this.useDynamicPricing && !this.selectedDate)
                return true; // ✅ CHANGED
            const anyQtySelected = Object.values(this.unitQuantities).some(q => q > 0);
            if (!anyQtySelected)
                return true;
            for (const [unitUuid, qty] of Object.entries(this.unitQuantities)) {
                if (qty > 0) {
                    const unit = this.findUnit(unitUuid);
                    if (!unit)
                        return true;
                    if (qty > this.getUnitRemainingStock(unit))
                        return true;
                }
            }
            return false;
        },
        sellingBadgeCount() {
            return Number(this.selectedVariant?.already_booked_count ??
                this.selectedVariant?.already_sold_count ??
                this.product?.already_booked_count ??
                this.product?.sold_count);
        },
        sellingBadgeText() {
            return this.product?.variants?.length > 1
                ? 'already booked'
                : 'already sold';
        },
        totalSelectedQty() {
            return Object.values(this.unitQuantities || {}).reduce((acc, q) => {
                const n = Number(q || 0);
                return acc + (Number.isFinite(n) ? n : 0);
            }, 0);
        },
        totalSelectedAmount() {
            if (!this.selectedVariant?.units?.length)
                return 0;
            // ✅ CHANGED: compute against effective units (prices already in dollars)
            const byId = new Map(this.effectiveUnits.map(u => [u.uuid, u])); // ✅
            let sum = 0;
            for (const [unitUuid, qty] of Object.entries(this.unitQuantities || {})) {
                const unit = byId.get(unitUuid);
                const q = Number(qty || 0);
                const p = Number(unit?.price);
                if (q > 0 && Number.isFinite(p))
                    sum += q * p;
            }
            return sum;
        },
        hasPrices() {
            return !!this.effectiveUnits?.some(u => Number.isFinite(Number(u.price))); // ✅ CHANGED
        },
        useDynamicPricing() {
            const units = (this.selectedVariant?.units || []);
            return units.some(u => !!u.pricing_details);
        },
        // ✅ CHANGED: normalize all prices to DOLLARS and respect selected day
        unitsWithEffectivePrice() {
            const units = (this.selectedVariant?.units || []);
            const d = fromISO(this.selectedDate);
            if (!d) {
                // If no date chosen, return units with normalized fallback price in dollars (keeps UI coherent)
                return units.map(u => ({ ...u, price: Number(u.price) / 100 }));
            }
            const dayKey = toISO(d);
            return units.map(u => {
                const pd = u.pricing_details;
                // 1) blackout => keep base (fallback) price (normalized to dollars)
                const isBlackout = !!pd?.blackouts?.some(g => g.days?.some(x => x.date === dayKey));
                if (isBlackout)
                    return { ...u, price: Number(u.price) / 100 };
                // 2) exception for this unit?
                const pid = u.product_unit_id;
                const exPrice = pd?.exceptions?.reduce((acc, g) => {
                    const day = g.days?.find(x => x.date === dayKey);
                    if (!day)
                        return acc;
                    if (pid != null) {
                        const hit = day.units?.find(v => v.product_unit_id === pid);
                        return hit ? hit.price / 100 : acc; // ✅ dollars
                    }
                    return day.units && day.units.length === 1 ? day.units[0].price / 100 : acc; // ✅ dollars
                }, null) ?? null;
                if (exPrice != null)
                    return { ...u, price: exPrice };
                // 3) normal for this unit?
                const normPrice = (() => {
                    const norm = pd?.normal || [];
                    if (pid != null) {
                        const hit = norm.find(n => n.product_unit_id === pid);
                        return hit ? hit.price / 100 : null; // ✅ dollars
                    }
                    return norm.length === 1 ? norm[0].price / 100 : null; // ✅ dollars
                })();
                return { ...u, price: normPrice != null ? normPrice : Number(u.price) / 100 };
            });
        },
        // ✅ CHANGED: a convenient alias used by the template to send into ProductUnit
        effectiveUnits() {
            return this.useDynamicPricing ? this.unitsWithEffectivePrice : (this.selectedVariant?.units || []).map((u) => ({ ...u, price: Number(u.price) / 100 }));
        },
    },
    data() {
        return {
            // isFavorite: false,
            addedToCartItem: null,
            disableAddToCart: false,
            unitQuantities: {},
            selectedDate: '',
            selectedTimeSlot: '',
            //wishlistLoading: false,
            addToCartLoading: false,
            isPriceConStick: false,
            validateMinPaxErrors: [],
        };
    },
    watch: {
        selectedVariant: {
            immediate: true,
            handler(variant) {
                const next = {};
                if (variant?.units?.length) {
                    variant.units.forEach((u) => { next[u.uuid] = 0; });
                }
                this.unitQuantities = next;
                // Optional: if variant changes, clear date to force re-selection when dynamic
                if (this.useDynamicPricing) {
                    this.selectedDate = '';
                    this.selectedTimeSlot = '';
                } // ✅ CHANGED (optional but keeps flow strict)
            },
        },
        unitQuantities: {
            deep: true,
            handler() {
                this.validateMinPax();
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.onScroll();
        });
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
    },
    methods: {
        ...mapActions('cartModule', ['addItem', 'getCartItems']),
        // ...mapActions('wishlistModule', ['addToWishList', 'removeWishlistItem']),
        ...mapActions('productModule', ['setCurrentVariant']),
        ...mapGetters('cartModule', ['ChangiRecommendsemByUnitUuid']),
        toLowerCase(value) {
            if (typeof value !== 'string')
                return '';
            return value.toLowerCase();
        },
        scrollToId(id) {
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            else {
                console.warn(`Element with id "${id}" not found.`);
            }
        },
        onDateSelected(iso) {
            const [date, timeSlot] = iso.split('::');
            // console.log('date', date);
            // console.log('timeSlot', timeSlot);
            this.selectedTimeSlot = timeSlot;
            this.selectedDate = date;
        },
        onScroll() {
            const y = window.scrollY || 0;
            const mq = window.matchMedia('(max-width: 991.98px)');
            if (!mq.matches) {
                this.isPriceConStick = (y > 500);
            }
            else {
                this.isPriceConStick = false;
            }
        },
        onUpdateUnitQty({ unitUuid, qty }) {
            this.$set(this.unitQuantities, unitUuid, qty);
            this.$emit('is-selected-option', { isAbleToAdd: !this.isAddToCartDisabled, totalAmount: this.totalSelectedAmount });
        },
        normalizeBool(v) {
            if (typeof v === 'boolean')
                return v;
            if (v === 1)
                return true;
            if (v === 0)
                return false;
            return !!v;
        },
        safeNumber(v, fallback = 0) {
            const n = Number(v);
            return Number.isFinite(n) ? n : fallback;
        },
        findUnit(unitUuid) {
            const unit = this.selectedVariant?.units?.find((u) => u.uuid === unitUuid);
            return this.selectedVariant?.units?.find((u) => u.uuid === unitUuid);
        },
        getUnitRemainingStock(u) {
            const avail = this.safeNumber(u.available_stocks, 0);
            const inCart = (this.items || []).find((item) => item.selectedUnitUuid === u.uuid);
            const inCartQty = this.safeNumber(inCart?.quantity, 0);
            const stockFlag = this.normalizeBool(u.is_in_stock);
            return stockFlag ? Math.max(0, avail - inCartQty) : 0;
        },
        requiresTimeSelection(variant) {
            const slots = variant?.time_slots_json;
            if (!Array.isArray(slots) || slots.length === 0)
                return false;
            // Require selection only if there is at least one non-empty "time"
            return slots.some((s) => {
                if (s == null)
                    return false;
                // If API sometimes returns an array of strings
                if (typeof s === 'string')
                    return s.trim().length > 0;
                // Typical case: array of objects { id, time, ... }
                if (typeof s === 'object')
                    return String(s.time ?? '').trim().length > 0;
                return false;
            });
        },
        async bookNow() {
            if (!this.validateMinPax()) {
                return;
            }
            await this.addToCart();
            await this.checkout();
        },
        async checkout() {
            this.$router.push('/checkout');
        },
        validateMinPax() {
            this.validateMinPaxErrors = [];
            this.selectedVariant.units.forEach(unit => {
                const uuid = unit.uuid;
                const minRequired = unit.min_pax;
                console.log(this.unitQuantities[uuid]);
                const currentQty = this.unitQuantities[uuid] || 0;
                if (currentQty < minRequired) {
                    const unitName = unit.name;
                    this.validateMinPaxErrors.push(`At least <b>${minRequired} ${unitName}</b> required.`);
                }
            });
            return this.validateMinPaxErrors.length > 0 ? false : true;
        },
        async addToCart() {
            if (!this.selectedVariant) {
                this.$bvToast.toast('Please select a variant.', { variant: 'warning' });
                return;
            }
            if (this.useDynamicPricing && !this.selectedDate) {
                this.$bvToast.toast('Please select a date first.', { variant: 'warning' });
                return;
            }
            const selectedTime = String(this.selectedTimeSlot ?? '').trim();
            if (this.useDynamicPricing && this.requiresTimeSelection(this.selectedVariant) && !selectedTime) {
                this.$bvToast.toast('Please select a time first.', { variant: 'warning' });
                return;
            }
            if (!this.validateMinPax()) {
                return;
            }
            const entries = Object.entries(this.unitQuantities).filter(([_, qty]) => qty > 0);
            if (entries.length === 0) {
                this.$bvToast.toast('Please choose at least 1 ticket.', { variant: 'warning' });
                return;
            }
            const payloads = [];
            for (const [unitUuid, qty] of entries) {
                const unit = this.findUnit(unitUuid);
                const remain = this.getUnitRemainingStock(unit);
                if (qty > remain) {
                    this.$bvToast.toast(`Only ${remain} left for ${unit.name || 'this unit'}.`, { variant: 'warning' });
                    continue;
                }
                payloads.push({ unitUuid, quantity: qty, date: this.selectedDate, time: this.selectedTimeSlot ?? undefined });
            }
            if (payloads.length === 0)
                return;
            if (this.addToCartLoading)
                return;
            this.addToCartLoading = true;
            const started = Date.now();
            const MIN_VISIBLE_MS = 400;
            await this.$nextTick();
            await new Promise(r => requestAnimationFrame(() => r()));
            this.disableAddToCart = true;
            try {
                const results = [];
                for (const p of payloads) {
                    try {
                        const value = await this.addItem(p);
                        results.push({ status: 'fulfilled', value });
                    }
                    catch (reason) {
                        results.push({ status: 'rejected', reason });
                    }
                }
                const anySuccess = results.some(r => r.status === 'fulfilled');
                results.forEach((r, idx) => {
                    const p = payloads[idx];
                    if (r.status === 'fulfilled')
                        this.$set(this.unitQuantities, p.unitUuid, 0);
                });
                if (anySuccess) {
                    const lastSuccessIdx = [...results].map(r => r.status).lastIndexOf('fulfilled');
                    const last = payloads[lastSuccessIdx];
                    this.addedToCartItem = this.cartItems.find((item) => item.selectedUnitUuid === last.unitUuid);
                }
                if (this.cartMessages && this.cartMessages.length > 0) {
                    this.$bvToast.toast(this.cartMessages.join(', '), { variant: 'info' });
                }
                else {
                    this.$bvToast.toast('Item added to cart', { variant: 'success', autoHideDelay: 1000 });
                }
                this.getCartItems();
            }
            catch (error) {
                const msg = error?.data?.message || 'Failed to add to cart';
                this.$bvToast.toast(msg, { variant: 'danger' });
                console.error(error);
            }
            finally {
                setTimeout(() => (this.disableAddToCart = false), 100);
                const elapsed = Date.now() - started;
                const waitMore = Math.max(0, MIN_VISIBLE_MS - elapsed);
                if (waitMore > 0)
                    await new Promise(r => setTimeout(r, waitMore));
                this.$emit('is-selected-option', { isAbleToAdd: this.isAddToCartDisabled, totalAmount: this.totalSelectedAmount });
                this.addToCartLoading = false;
            }
        },
        nl2br(val) {
            if (val == null)
                return '';
            return String(val).replace(/\r\n|\n/g, '<br>');
        },
    },
});
